import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Alert, CircularProgress, TextField,
  List, ListItemButton, ListItemIcon, ListItemText, Checkbox, Paper,
  Typography,
} from '@mui/material';
import { useGenerarCvuLote, useEmpresasSinCvu, useCvuConfig } from '../../hooks/useCvus';
import { formatCuit } from '../../utils/format';
import type { CvuLoteResult } from '../../types/cvu';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: (result: CvuLoteResult) => void;
}

const MAX_BATCH = 5;

export default function GenerarLoteDialog({ open, onClose, onSuccess }: Props) {
  const { data: empresas = [], isLoading } = useEmpresasSinCvu();
  const { data: config } = useCvuConfig();
  const generar = useGenerarCvuLote();

  const [seleccionados, setSeleccionados] = useState<string[]>([]);
  const [idLote, setIdLote] = useState('');

  useEffect(() => {
    if (open) {
      setSeleccionados([]);
      setIdLote(`LOTE-${new Date().toISOString().slice(0, 10)}-${Date.now() % 1000}`);
      generar.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (id: string) => {
    setSeleccionados((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : prev.length >= MAX_BATCH
          ? prev
          : [...prev, id],
    );
  };

  const handleSubmit = async () => {
    if (seleccionados.length === 0) return;
    // Trim el idLote: evita espacios sueltos en el header de auditoría (#031)
    const loteLimpio = idLote.trim();
    try {
      const r = await generar.mutateAsync({
        empresa_ids: seleccionados,
        id_lote:     loteLimpio || undefined,
      });
      onSuccess?.(r);
      onClose();
    } catch {
      // se muestra abajo
    }
  };

  const apiError = generar.error as Error | null;
  const atLimit  = seleccionados.length >= MAX_BATCH;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Generar CVUs por lote (máx. {MAX_BATCH})
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {empresas.length === 0 && !isLoading && (
            <Alert severity="info">
              Todas las empresas activas ya tienen una CVU asignada.
            </Alert>
          )}

          {apiError && (
            <Alert severity="error">{apiError.message}</Alert>
          )}

          {config?.mode === 'mock' && (
            <Alert severity="warning" sx={{ fontSize: 13 }}>
              Modo MOCK: los CVUs se generan sintéticos.
            </Alert>
          )}

          <TextField
            label="ID de lote"
            fullWidth
            value={idLote}
            onChange={(e) => setIdLote(e.target.value)}
            helperText="Identificador para auditoría del envío"
            inputProps={{ maxLength: 50 }}
          />

          <Paper variant="outlined" sx={{ maxHeight: 320, overflow: 'auto' }}>
            <List dense disablePadding>
              {empresas.map((e) => {
                const checked = seleccionados.includes(e.id);
                const disabledRow = !checked && atLimit;
                return (
                  <ListItemButton
                    key={e.id}
                    onClick={() => toggle(e.id)}
                    disabled={disabledRow}
                  >
                    <ListItemIcon>
                      <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                    </ListItemIcon>
                    <ListItemText
                      primary={e.razon_social}
                      secondary={formatCuit(e.cuit)}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>

          <Typography variant="caption" color={atLimit ? 'warning.main' : 'text.secondary'}>
            {seleccionados.length}/{MAX_BATCH} seleccionada(s)
            {atLimit && ' — límite del batch'}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={generar.isPending}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={seleccionados.length === 0 || generar.isPending}
          startIcon={generar.isPending ? <CircularProgress size={18} /> : null}
        >
          {generar.isPending
            ? 'Generando…'
            : `Generar ${seleccionados.length || ''} CVU${seleccionados.length === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
